import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/Procurement.jsx (Purchase
// Requests list). `status` is not list-item-backed (PurchaseRequestController
// has no Rule::exists for it — the values are the fixed approval-chain
// states from PurchaseRequestController::STEP_KEYS/finalize()), so
// STATUS_LABELS stays a hardcoded map.
const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  pending_second_approval: 'Pending Second Approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

function statusBadgeClass(status: string): string {
  if (status === 'approved') return 'badge-normal';
  if (status === 'rejected') return 'badge-important';
  return 'badge-neutral';
}

interface PurchaseRequestRow {
  id: number;
  reference: string;
  title: string;
  item_name: string;
  estimated_cost: number;
  status: string;
  requester: { full_name: string } | null;
}

export function Procurement() {
  const { settings } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const filters = useMemo(
    () => ({ status: status || undefined, search: search || undefined }),
    [status, search]
  );
  const { data, loading, error, setPage, reload: load } = usePaginatedList<PurchaseRequestRow>(
    '/purchase-requests',
    filters
  );

  const remove = async (pr: PurchaseRequestRow) => {
    if (!(await confirmDelete(`Delete draft request "${pr.title}"?`))) return;
    try {
      await api.delete(`/purchase-requests/${pr.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<PurchaseRequestRow>(
      api,
      '/purchase-requests',
      { status: status || undefined, search: search || undefined },
      [
        { label: 'Request No', value: (r) => r.reference },
        { label: 'Title', value: (r) => r.title },
        { label: 'Item', value: (r) => r.item_name },
        { label: 'Requester', value: (r) => r.requester?.full_name ?? '' },
        { label: 'Estimated Cost', value: (r) => r.estimated_cost },
        { label: 'Status', value: (r) => STATUS_LABELS[r.status] ?? r.status },
      ],
      'purchase-requests.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Requests</h1>
        <Link className="btn btn-primary" to="/procurement/requests/new">
          + Add Request
        </Link>
      </header>
      <p className="muted">
        Purchase requests, quotations, and approvals — before an item is purchased and registered.
      </p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search reference, title or item…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Request No</th>
                <th>Item</th>
                <th>Requester</th>
                <th className="right">Est. Cost</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((pr) => (
                  <tr key={pr.id}>
                    <td className="muted">{pr.reference}</td>
                    <td>
                      <strong>{pr.item_name}</strong>
                      <div>
                        <small className="muted">{pr.title}</small>
                      </div>
                    </td>
                    <td>{pr.requester?.full_name}</td>
                    <td className="right">{fmtMoney(pr.estimated_cost)}</td>
                    <td>
                      <span className={`badge ${statusBadgeClass(pr.status)}`}>
                        {STATUS_LABELS[pr.status] ?? pr.status}
                      </span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/procurement/requests/${pr.id}`} />
                      <IconButton
                        icon={IconTrash}
                        label="Delete"
                        variant="danger-ghost"
                        onClick={() => remove(pr)}
                        hidden={pr.status !== 'draft'}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No purchase requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
